/**
 * Thin fetch wrapper over the staff API.
 *
 * Two things it guarantees, so no caller has to think about them:
 *   - the session cookie always rides along (`credentials: 'include'`);
 *   - a non-2xx response becomes an ApiError carrying the server's own
 *     machine code and human message, so UI never has to invent wording for
 *     a failure the backend already explained.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the fix is "sign in again", which the shell handles globally. */
  get isAuthProblem(): boolean {
    return this.status === 401 && this.code === 'STAFF_UNAUTHENTICATED';
  }

  /** Field-level problems from a zod validation failure, if any. */
  get fieldProblems(): Array<{ path: string; message: string }> {
    if (!Array.isArray(this.details)) return [];
    return this.details.filter(
      (d): d is { path: string; message: string } =>
        typeof d === 'object' && d !== null && 'path' in d && 'message' in d,
    );
  }
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    // A network failure has no server message, so this is the one place the
    // client writes the wording itself.
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      'Could not reach the Heron License server. Check that it is running, then try again.',
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = undefined;
    }
  }

  if (!response.ok) {
    const envelope = payload as { error?: { code?: string; message?: string; details?: unknown } };
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? 'INTERNAL_ERROR',
      envelope?.error?.message ?? `Request failed (${response.status}).`,
      envelope?.error?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

/** Builds `?a=1&b=2`, omitting empty values so the URL stays readable. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const serialised = search.toString();
  return serialised ? `?${serialised}` : '';
}
